"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProductDetails() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const components = [{
    id: "frame",
    label: "Frame",
    number: "01",
    name: "Reinforced Chassis",
    description: "Precision-engineered aerospace aluminum frame, anodized for maximum durability. Designed to withstand extreme pressure while maintaining a lightweight profile.",
    specs: [{
      label: "Material",
      value: "Al-6061"
    }, {
      label: "Weight",
      value: "450g"
    }, {
      label: "Finish",
      value: "Matte"
    }],
    top: "32%",
    left: "82%"
  }, {
    id: "lens",
    label: "Optical Lens",
    number: "02",
    name: "High-Clarity Glass",
    description: "Multi-coated optical glass providing superior light transmission and scratch resistance. Engineered for distortion-free viewing in all lighting conditions.",
    specs: [{
      label: "Clarity",
      value: "99.9%"
    }, {
      label: "Thickness",
      value: "3.2 mm"
    }, {
      label: "Coating",
      value: "Anti-Glare"
    }],
    top: "42%",
    left: "48%"
  }, {
    id: "sensor",
    label: "Sensor Array",
    number: "03",
    name: "Precision Sensor",
    description: "High-sensitivity digital sensor array calibrated for micro-second response times. Integrated directly into the core for seamless data acquisition.",
    specs: [{
      label: "Resolution",
      value: "4K UHD"
    }, {
      label: "Latency",
      value: "<1ms"
    }, {
      label: "Interface",
      value: "Digital"
    }],
    top: "55%",
    left: "38%"
  }, {
    id: "mount",
    label: "Mounting Base",
    number: "04",
    name: "Universal Mount",
    description: "Forged steel mounting base with vibration-dampening technology. Ensures rock-solid stability during high-intensity operational cycles.",
    specs: [{
      label: "Construction",
      value: "Forged"
    }, {
      label: "Width",
      value: "45 mm"
    }, {
      label: "Finish",
      value: "Hardened"
    }],
    top: "68%",
    left: "62%"
  }, {
    id: "casing",
    label: "Outer Casing",
    number: "05",
    name: "Protective Shell",
    description: "Impact-resistant polymer shell with ergonomic grip texture. Designed to protect internal components from environmental hazards and accidental drops.",
    specs: [{
      label: "Material",
      value: "Polymer"
    }, {
      label: "Thickness",
      value: "2.5 mm"
    }, {
      label: "Texture",
      value: "Anti-Slip"
    }],
    top: "84%",
    left: "28%"
  }];
  const [activeId, setActiveId] = useState<string>("lens");
  const active = components.find(c => c.id === activeId) ?? components[1];
  return <section data-section-id="5188" id="product-details" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      <div ref={ref} className={`mx-auto max-w-[1400px] px-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div className="relative lg:col-span-7 lg:-ml-6">
            <div className="relative w-full h-[60vh] sm:h-[75vh] lg:h-[90vh] overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_sensor_macro.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_sensor_macro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_sensor_macro.png-wvc-sizes" alt="Industrial sensor macro detail" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-background/40" />

              {}
              <div className="hidden lg:block">
                {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} aria-label={`View details for ${c.label}`} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{
                top: c.top,
                left: c.left
              }}>
                    <span className={`absolute inset-0 -m-3 rounded-lg ${activeId === c.id ? "animate-ping bg-primary/30" : "bg-primary/10 animate-pulse"}`} style={{
                  borderRadius: "9999px"
                }} />
                    <span className={`relative block size-3 rounded-lg border transition-all duration-300 ${activeId === c.id ? "bg-primary border-primary scale-125" : "bg-primary/60 border-primary/80 group-hover:scale-110"}`} style={{
                  borderRadius: "9999px"
                }} />
                  </button>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pt-8">
            <div className="font-default text-xs uppercase tracking-widest text-primary mb-6">
              Engineering / 03
            </div>

            <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-foreground mb-10">
              The <em className="italic font-default">Precision</em>
              <br />
              Architecture
            </h2>

            <p className="font-default text-base text-muted-foreground leading-relaxed max-w-md mb-12">
              Every component is engineered for peak performance. Explore the 
              technical specifications that define our industrial standards.
            </p>

            {}
            <div key={active.id} className="relative border border-border p-8 lg:p-10 transition-opacity duration-400 animate-in fade-in">
              <div className="absolute -top-3 left-8 bg-background px-3 font-default text-[10px] uppercase tracking-[0.25em] text-primary">
                {active.label} / {active.number}
              </div>

              <h3 className="font-default text-3xl lg:text-4xl text-foreground mb-4 leading-tight">
                {active.name}
              </h3>

              <p className="font-default text-sm text-muted-foreground leading-relaxed mb-8">
                {active.description}
              </p>

              {}
              <div className="border border-border/60 bg-card/40 p-5 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                    Fig. {active.number}
                  </span>
                  <Plus className="size-3 text-muted-foreground" />
                </div>
                <div className="relative h-24 flex items-center justify-center">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
                  <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                  <div className="relative size-16 border border-primary/60 rounded-lg flex items-center justify-center">
                    <div className="size-8 border border-primary/40 rounded-lg" />
                  </div>
                </div>
              </div>

              {}
              <div className="space-y-3">
                {active.specs.map((s, i) => <div key={s.label} data-index={i} className="flex items-baseline justify-between border-b border-border/50 pb-3 last:border-b-0">
                    <span className="font-default text-[11px] uppercase tracking-widest text-muted-foreground">
                      {s.label}
                    </span>
                    <span className="font-default text-sm tabular-nums text-foreground">
                      {s.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="hidden lg:grid grid-cols-5 mt-20 border-t border-border pt-8">
          {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} className={`group text-left transition-colors duration-300 ${activeId === c.id ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <div className="font-default text-[10px] uppercase tracking-[0.3em] mb-2">
                {c.number}
              </div>
              <div className="font-default text-xs uppercase tracking-widest">
                {c.label}
              </div>
              <div className={`mt-3 h-px transition-all duration-500 ${activeId === c.id ? "bg-primary w-full" : "bg-border w-6 group-hover:w-full group-hover:bg-foreground/40"}`} />
            </button>)}
        </div>

        {}
        <div className="lg:hidden mt-10 -mx-6 px-6 overflow-x-auto">
          <div className="flex gap-3 pb-2 w-max">
            {components.map((c, i) => <button key={c.id} data-index={i} onClick={() => setActiveId(c.id)} className={`shrink-0 border px-4 py-3 text-left transition-colors duration-300 ${activeId === c.id ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
                <div className="font-default text-[10px] uppercase tracking-[0.25em] mb-1">
                  {c.number}
                </div>
                <div className="font-default text-xs uppercase tracking-widest">
                  {c.label}
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}